import { cacheGet, cacheSet } from './cache';

const YT = 'https://www.googleapis.com/youtube/v3';
const TTL_COMMERCIALS = 24 * 60 * 60 * 1000;    // 24 hours
const TTL_TRAILER     =  7 * 24 * 60 * 60 * 1000; // 7 days

function normalize(str) {
  return str.toLowerCase()
    .replace(/[\u064B-\u065F]/g, '')  // strip Arabic diacritics
    .replace(/[أإآا]/g, 'ا')          // unify alef variants
    .replace(/ة/g, 'ه')               // unify teh marbuta
    .replace(/ى/g, 'ي');              // unify alef maqsura
}

function titlesMatch(videoTitle, title, titleEn) {
  const vn = normalize(videoTitle);
  const check = (t, minLen) => {
    const words = normalize(t).split(/\s+/).filter(w => w.length > minLen);
    if (!words.length) return false;
    const matched = words.filter(w => vn.includes(w)).length;
    return matched / words.length >= 0.4;
  };
  return check(title, 2) || (titleEn ? check(titleEn, 3) : false);
}

export async function searchYouTubeTrailer(key, title, titleEn) {
  const cacheKey = `yt_trailer_${title}`;
  const cached = cacheGet(cacheKey);
  if (cached !== null) return cached;

  const q = encodeURIComponent(`${title} trailer`);
  const res = await fetch(`${YT}/search?part=snippet&q=${q}&type=video&maxResults=1&key=${key}`);
  const data = await res.json();
  const item = (data.items || [])[0];
  if (!item) { cacheSet(cacheKey, null, TTL_TRAILER); return null; }
  const result = titlesMatch(item.snippet.title, title, titleEn)
    ? { key: item.id.videoId, name: item.snippet.title }
    : null;
  cacheSet(cacheKey, result, TTL_TRAILER);
  return result;
}

export async function fetchYouTubeCommercials(key, year) {
  const cacheKey = `yt_commercials_${year}`;
  const cached = cacheGet(cacheKey);
  if (cached !== null) return cached;

  const q = encodeURIComponent(`إعلانات رمضان ${year} مصر`);
  const publishedAfter = `${year}-01-01T00:00:00Z`;
  const publishedBefore = `${year}-12-31T23:59:59Z`;
  const res = await fetch(`${YT}/search?part=snippet&q=${q}&type=video&maxResults=20&key=${key}&relevanceLanguage=ar&publishedAfter=${publishedAfter}&publishedBefore=${publishedBefore}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error.message);
  const MIN_SUBSCRIBERS = 10_000;
  const isRamadan = v => {
    const text = `${v.snippet.title} ${v.snippet.description}`.toLowerCase();
    return text.includes('رمضان') || text.includes('ramadan');
  };
  const ramadanItems = (data.items || []).filter(isRamadan);

  const channelIds = [...new Set(ramadanItems.map(v => v.snippet.channelId))].join(',');
  const chRes = await fetch(`${YT}/channels?part=statistics&id=${channelIds}&key=${key}`);
  const chData = await chRes.json();
  const subscriberCount = {};
  (chData.items || []).forEach(ch => {
    subscriberCount[ch.id] = parseInt(ch.statistics.subscriberCount || '0', 10);
  });

  const result = ramadanItems
    .filter(v => (subscriberCount[v.snippet.channelId] || 0) >= MIN_SUBSCRIBERS)
    .map(v => ({
      id: v.id.videoId,
      mediaType: 'youtube',
      youtubeId: v.id.videoId,
      title: v.snippet.title,
      description: v.snippet.description,
      poster: v.snippet.thumbnails.high?.url || v.snippet.thumbnails.default?.url || null,
      channel: v.snippet.channelTitle,
      fromYoutube: true,
    }));
  cacheSet(cacheKey, result, TTL_COMMERCIALS);
  return result;
}
